/**
 * A2UI Renderer
 * Main component for rendering A2UI messages
 * Handles streaming JSONL parsing, surface management, and action callbacks
 * Based on Google A2UI Protocol v0.8
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import A2UISurface from './A2UISurface';
import { dataModelStore } from './DataModelStore';
import './A2UIStyles.css';

/**
 * Parse a single A2UI message from JSONL
 */
const parseA2UIMessage = (line) => {
  try {
    if (typeof line === 'string') {
      return JSON.parse(line);
    }
    return line;
  } catch (e) {
    console.error('A2UI: Failed to parse message:', e);
    return null;
  }
};

/**
 * Check if content contains A2UI messages
 */
export const isA2UIContent = (content) => {
  if (!content) return false;
  
  // Check if it's a string that looks like JSONL with A2UI messages
  if (typeof content === 'string') {
    return content.includes('"surfaceUpdate"') || 
           content.includes('"beginRendering"') ||
           content.includes('"dataModelUpdate"');
  }
  
  // Check if it's an object with A2UI message types
  if (typeof content === 'object') {
    return content.surfaceUpdate || 
           content.beginRendering || 
           content.dataModelUpdate ||
           content.deleteSurface;
  }
  
  return false;
};

/**
 * Parse JSONL string into array of messages
 */
export const parseA2UIStream = (content) => {
  if (!content) return [];
  
  if (typeof content === 'string') {
    return content
      .split('\n')
      .filter(line => line.trim())
      .map(parseA2UIMessage)
      .filter(msg => msg !== null);
  }
  
  if (Array.isArray(content)) {
    return content.map(parseA2UIMessage).filter(msg => msg !== null);
  }
  
  return [content];
};

const A2UIRenderer = ({ 
  messages = [],
  content,
  onAction,
  onError,
  className = '',
  style = {}
}) => {
  // Surface state: Map of surfaceId -> { components, rootId, catalogId, styles, isReady }
  const [surfaces, setSurfaces] = useState(new Map());
  const processedMessagesRef = useRef(new Set());

  // Process incoming messages
  const processMessage = useCallback((message) => {
    if (!message) return;

    // Handle surfaceUpdate
    if (message.surfaceUpdate) {
      const { surfaceId = 'default', components = [] } = message.surfaceUpdate;
      
      setSurfaces(prev => {
        const newSurfaces = new Map(prev);
        const existing = newSurfaces.get(surfaceId) || {
          components: [],
          rootId: null,
          catalogId: 'default',
          styles: {},
          isReady: false
        };
        
        // Merge new components with existing (update by ID or add new)
        const componentMap = new Map(existing.components.map(c => [c.id, c]));
        components.forEach(comp => {
          componentMap.set(comp.id, comp);
        });
        
        newSurfaces.set(surfaceId, {
          ...existing,
          components: Array.from(componentMap.values())
        });
        
        return newSurfaces;
      });
    }

    // Handle dataModelUpdate
    if (message.dataModelUpdate) {
      dataModelStore.processDataModelUpdate(message.dataModelUpdate);
    }

    // Handle beginRendering
    if (message.beginRendering) {
      const { surfaceId = 'default', root, catalogId, styles } = message.beginRendering;
      
      setSurfaces(prev => {
        const newSurfaces = new Map(prev);
        const existing = newSurfaces.get(surfaceId) || {
          components: [],
          rootId: null,
          catalogId: 'default',
          styles: {},
          isReady: false
        };
        
        newSurfaces.set(surfaceId, {
          ...existing,
          rootId: root,
          catalogId: catalogId || existing.catalogId,
          styles: styles || existing.styles,
          isReady: true
        });
        
        return newSurfaces;
      });
    }

    // Handle deleteSurface
    if (message.deleteSurface) {
      const { surfaceId } = message.deleteSurface;
      
      setSurfaces(prev => {
        const newSurfaces = new Map(prev);
        newSurfaces.delete(surfaceId);
        return newSurfaces;
      });
      
      dataModelStore.deleteSurface(surfaceId);
    }
  }, []);

  // Process messages array
  useEffect(() => {
    const allMessages = content ? parseA2UIStream(content) : messages;
    
    allMessages.forEach((msg, index) => {
      const msgKey = JSON.stringify(msg) + index;
      if (!processedMessagesRef.current.has(msgKey)) {
        processedMessagesRef.current.add(msgKey);
        processMessage(msg);
      }
    });
  }, [messages, content, processMessage]);

  // Handle user actions
  const handleAction = useCallback((action) => {
    if (onAction) {
      onAction(action);
    } else {
      console.log('A2UI Action:', action);
    }
  }, [onAction]);

  // Render all surfaces
  const surfaceArray = Array.from(surfaces.entries());

  if (surfaceArray.length === 0) {
    return null;
  }

  return (
    <div className={`a2ui-renderer ${className}`} style={style}>
      {surfaceArray.map(([surfaceId, surfaceData]) => (
        <A2UISurface
          key={surfaceId}
          surfaceId={surfaceId}
          components={surfaceData.components}
          rootId={surfaceData.rootId}
          catalogId={surfaceData.catalogId}
          styles={surfaceData.styles}
          isReady={surfaceData.isReady}
          onAction={handleAction}
        />
      ))}
    </div>
  );
};

/**
 * Hook for processing A2UI messages from SSE stream
 */
export const useA2UIStream = (initialMessages = []) => {
  const [messages, setMessages] = useState(initialMessages);

  const addMessage = useCallback((message) => {
    const parsed = parseA2UIMessage(message);
    if (parsed) {
      setMessages(prev => [...prev, parsed]);
    }
  }, []);

  const addMessages = useCallback((newMessages) => {
    const parsed = parseA2UIStream(newMessages);
    setMessages(prev => [...prev, ...parsed]);
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  return { messages, addMessage, addMessages, clearMessages };
};

export default A2UIRenderer;
